import { Table } from "@tanstack/react-table";
import { CheckCircle2, CircleDashed, ListFilter } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { CUSTOM_FILTERS } from "@/config/list";

type HasValueState = "all" | "configured" | "missing";

const toggleStateFromFilter = (filterValue: unknown): HasValueState => {
	if (filterValue === true) return "configured";
	if (filterValue === false) return "missing";
	return "all";
};

const filterValueFromState = (state: HasValueState): boolean | undefined => {
	if (state === "configured") return true;
	if (state === "missing") return false;
	return undefined;
};

type Props<TData> = {
	table: Table<TData>;
};

export const PropertyHasValueToggle = <TData,>({ table }: Props<TData>) => {
	const tableState = table.getState();
	const currentFilterValue = tableState.columnFilters.find(
		columnFilter => columnFilter.id === CUSTOM_FILTERS.propertyHasValue,
	)?.value;
	const currentToggleState = toggleStateFromFilter(currentFilterValue);

	const handleValueChange = (nextState: string) => {
		// Radix calls with "" when clicking the already-active item; treat that
		// as a reset to "all".
		const resolvedState = (nextState || "all") as HasValueState;
		const nextFilterValue = filterValueFromState(resolvedState);
		table.setColumnFilters(previousFilters => {
			const otherFilters = previousFilters.filter(
				columnFilter => columnFilter.id !== CUSTOM_FILTERS.propertyHasValue,
			);
			return nextFilterValue === undefined
				? otherFilters
				: [...otherFilters, { id: CUSTOM_FILTERS.propertyHasValue, value: nextFilterValue }];
		});
	};

	return (
		<ToggleGroup
			type="single"
			value={currentToggleState}
			onValueChange={handleValueChange}
			variant="outline"
			size="sm"
			className="gap-0"
		>
			<ToggleGroupItem value="all" aria-label="Show all properties" className="rounded-r-none">
				<ListFilter className="h-3.5 w-3.5 mr-1" />
				All
			</ToggleGroupItem>
			<ToggleGroupItem
				value="configured"
				aria-label="Show only configured properties"
				className="rounded-none border-x-0"
			>
				<CheckCircle2 className="h-3.5 w-3.5 mr-1" />
				Configured
			</ToggleGroupItem>
			<ToggleGroupItem value="missing" aria-label="Show only missing properties" className="rounded-l-none">
				<CircleDashed className="h-3.5 w-3.5 mr-1" />
				Missing
			</ToggleGroupItem>
		</ToggleGroup>
	);
};

export const getTablePropertyHasValueFilter = (filters?: { id: string; value: unknown }[]): boolean | undefined => {
	const filterEntry = filters?.find(columnFilter => columnFilter.id === CUSTOM_FILTERS.propertyHasValue);
	if (filterEntry?.value === true) return true;
	if (filterEntry?.value === false) return false;
	return undefined;
};
